/**
 * Typed HTTP client for the Natura Tagger backend.
 * - Base URL comes from runtime config (/config.json).
 * - Auth token is pulled from Firebase Auth at request time (always fresh).
 */
import { getConfig } from "@/config";
import { getFirebaseAuth } from "@/auth/firebase";

const CONTRACT_VERSION = "1.0";

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message: string,
  ) {
    super(message);
  }
}

async function authHeader(): Promise<Record<string, string>> {
  if (getConfig().devAuthBypass) return {};
  const user = getFirebaseAuth().currentUser;
  if (!user) return {};
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const base = getConfig().apiBaseUrl.replace(/\/$/, "");
  const headers: Record<string, string> = {
    "X-Contract-Version": CONTRACT_VERSION,
    ...(init.headers as Record<string, string> | undefined),
    ...(await authHeader()),
  };
  // Only set JSON content-type when we're sending a JSON body.
  if (init.body && !(init.body instanceof FormData)) {
    headers["Content-Type"] ??= "application/json";
  }
  const response = await fetch(`${base}${path}`, { ...init, headers });
  if (!response.ok) {
    const body = await response
      .json()
      .catch(() => ({ detail: response.statusText }));
    throw new ApiError(response.status, body, `${response.status} ${path}`);
  }
  // 204 No Content / 205 Reset Content / HEAD have no body; callers that
  // expect void should annotate as Promise<void> and ignore the value.
  if (response.status === 204 || response.status === 205) {
    return undefined as T;
  }
  return (await response.json()) as T;
}
